import { Typography } from '@mui/material';
import React from 'react';
import { themeColors } from '../JuvareTheme';
/**
 * Apply styles to standard HTML elements used by markdown.
 * Children are expected to be standard HTML elements like: h1, h2, p, a, table, ul, ol, hr, blockquote, etc...
 *
 * @param {object} props - Component props.
 * @param {'h1'|'h2'|'h3'|'h4'|'h5'|'h6'} [props.firstHeading='h3'] - The MUI heading level to use as the first-level heading.
 * @param {'body1'|'body2'} [props.textStyle='body1'] - The MUI text variant to apply to non-heading content.
 * @param {string} [props.textColor] - Optional color for text.
 * @param {string} [props.className] - Optional class name for the root element.
 * @param {SxProps<Theme>} [props.sx] - Optional `sx` override for the root element.
 * @param {string} props.'data-test' - Optional test ID attribute.
 * @param {React.ReactNode} props.children - Child elements to render and style.
 *
 * @returns {React.ReactElement} A styled Typography wrapper for markdown content.
 */
export const MarkdownStyle = ({ firstHeading = 'h3', textStyle = 'body1', textColor, className, sx, 'data-test': dataTest, children, }) => {
    let h;
    switch (firstHeading) {
        case 'h1':
            h = ['h1', 'h2', 'h3', 'h4', 'h5'];
            break;
        case 'h2':
            h = ['h2', 'h3', 'h4', 'h5'];
            break;
        case 'h3':
            h = ['h3', 'h4', 'h5'];
            break;
        case 'h4':
            h = ['h4', 'h5'];
            break;
        case 'h5':
            h = ['h5'];
            break;
        case 'h6':
            h = [];
            break;
    }
    return (React.createElement(Typography, { component: 'div', variant: textStyle, className: className, "data-test": dataTest, sx: [
            (theme) => {
                var _a, _b, _c, _d, _e, _f;
                return ({
                    color: textColor,
                    '& h1': Object.assign(Object.assign({}, theme.typography[((_a = h[0]) !== null && _a !== void 0 ? _a : 'h6')]), marginAndPadding),
                    '& h2': Object.assign(Object.assign({}, theme.typography[((_b = h[1]) !== null && _b !== void 0 ? _b : 'h6')]), marginAndPadding),
                    '& h3': Object.assign(Object.assign({}, theme.typography[((_c = h[2]) !== null && _c !== void 0 ? _c : 'h6')]), marginAndPadding),
                    '& h4': Object.assign(Object.assign({}, theme.typography[((_d = h[3]) !== null && _d !== void 0 ? _d : 'h6')]), marginAndPadding),
                    '& h5': Object.assign(Object.assign({}, theme.typography[((_e = h[4]) !== null && _e !== void 0 ? _e : 'h6')]), marginAndPadding),
                    '& h6': Object.assign(Object.assign({}, theme.typography[((_f = h[5]) !== null && _f !== void 0 ? _f : 'h6')]), marginAndPadding),
                    '& p': Object.assign({}, marginAndPadding),
                    '& hr': Object.assign(Object.assign({}, marginAndPadding), { border: 'none', borderBottom: '1px solid ' + theme.palette.divider }),
                    '& blockquote': Object.assign(Object.assign({}, marginAndPadding), { p: 1, pl: 3, borderLeft: '2px solid ' + theme.palette.divider }),
                    '& pre': Object.assign(Object.assign({}, marginAndPadding), { p: 1, border: '1px solid ' + theme.palette.divider, borderRadius: 2, backgroundColor: theme.palette.background.paper, overflowY: 'auto' }),
                    '& table': Object.assign(Object.assign({}, marginAndPadding), { borderCollapse: 'collapse' }),
                    '& table th, & table td': {
                        pt: 1,
                        pb: 1,
                        pl: 2,
                        pr: 2,
                        border: '1px solid ' + theme.palette.divider,
                        textAlign: 'left',
                    },
                    '& table th[align=right], & table td[align=right]': {
                        textAlign: 'right',
                    },
                    '& a': {
                        color: themeColors.text.link,
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    },
                });
            },
            ...(Array.isArray(sx) ? sx : [sx]),
        ] }, children));
};
const marginAndPadding = {
    m: 0,
    p: 0,
    mb: 2,
    '&:last-child': {
        mb: 0,
    },
};
